import { createApp } from "vue";
import Axios from "axios";
import App from "./App.vue";
import router from "./router";
import store from "./store";

// sweetalert
import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

// toaster
import VueToast from "vue-toast-notification";
import "vue-toast-notification/dist/theme-default.css";

// loader
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

/* My Plugins */
import alerts from "@/plugins/alerts";
import ApiService from "@/plugins/api";
import HTTP from "@/plugins/http";
import Loader from "@/plugins/loader";

// css files
import "./assets/styles/sass/custom.scss";
import "./assets/dist/custom.css";

// bootstrap files
import "bootstrap-icons/font/bootstrap-icons.css";
import "jquery/src/jquery.js";
import "popper.js/dist/popper.min.js";
import "bootstrap/dist/js/bootstrap.min.js";

// runtime config
import { loadConfig, getConfig, getAuthCheckUrl } from "./config";
import { debugError, debugLog } from "./plugins/debug";

async function initApp() {

  /**
   * ---------------------------------------------------------
   * 1️⃣ Load runtime configuration
   * ---------------------------------------------------------
   * config.json is loaded before the app starts so that:
   * - API URLs
   * - OAuth endpoints
   * - environment settings
   * can change without rebuilding the frontend.
   */
  await loadConfig();
  debugLog("Runtime config loaded");


  /**
   * ---------------------------------------------------------
   * 2️⃣ Configure Axios
   * ---------------------------------------------------------
   * All API calls will use the base URL defined in config.json.
   * Example:
   *   Axios.get("/auth/me")
   * becomes:
   *   /api/auth/me
   *
   * withCredentials = true ensures cookies from oauth2-proxy
   * are sent with requests.
   */
  Axios.defaults.baseURL = getConfig("API_BASE_URL") || "/";
  Axios.defaults.withCredentials = true;

  debugLog("Axios base URL:", Axios.defaults.baseURL);


  /**
   * ---------------------------------------------------------
   * 3️⃣ OAuth2 Authentication Detection
   * ---------------------------------------------------------
   *
   * Authentication architecture:
   *
   * Browser
   *   │
   *   ▼
   * oauth2-proxy
   *   │
   *   ▼
   * Keycloak (Identity Provider)
   *   │
   *   ▼
   * .NET API
   *
   * Flow explanation:
   *
   * 1. User opens the Vue application on localhost
   *
   * 2. Vue calls:
   *      /oauth2/auth
   *
   * 3. oauth2-proxy checks if a valid session cookie exists.
   *
   *      If NOT authenticated:
   *          → returns 401
   *
   *      If authenticated:
   *          → returns 202
   *
   * 4. If 202 is returned, Vue calls the backend endpoint:
   *      /api/auth/me
   *
   * 5. The backend receives identity headers injected by
   *    oauth2-proxy, such as:
   *
   *      X-Forwarded-User
   *      X-Forwarded-Email
   *      X-Forwarded-Groups
   *
   * 6. .NET converts those headers into Claims and returns
   *    the user profile.
   *
   * 7. Vue stores the user profile in Vuex for use in the UI.
   */

  try {

    const authCheckUrl = getAuthCheckUrl();
    debugLog("Auth check URL:", authCheckUrl);

    /**
     * Step 1:
     * Ask oauth2-proxy if the user is already authenticated.
     */
    const authProbe = await fetch(authCheckUrl, {
      credentials: "include"
    });

    /**
     * oauth2-proxy behavior:
     *
     * 202 → user authenticated
     * 401 → user not logged in
     */
    if (authProbe.status === 202) {

      /**
       * Step 2:
       * Fetch authenticated user profile from backend.
       */
      const profileEndpoint = getConfig("AUTH.PROFILE_ENDPOINT");

      debugLog("Fetching user profile from:", profileEndpoint);

      const profileResponse = await Axios.get(profileEndpoint);
      const profile = profileResponse?.data || {};

      /**
       * Step 3:
       * Save user information in Vuex store.
       * This allows components to access:
       *   - user name
       *   - email
       *   - roles
       *   - groups
       */
      await store.dispatch("saveAPIauth", {
        ...profile,
        UserName:
          profile.fullName ||
          profile.preferredUsername ||
          profile.username ||
          ""
      });

      debugLog("User profile loaded:", profile);


      /**
       * ---------------------------------------------------------
       * Restore pending route after login
       * ---------------------------------------------------------
       *
       * When a user tries to access a protected page before login,
       * we store the requested route in sessionStorage:
       *
       *    app3_post_login_target
       *
       * After authentication succeeds, we redirect the user back
       * to that route.
       *
       * Example:
       *   User visits /services
       *   → not authenticated
       *   → redirected to Keycloak
       *
       * After login:
       *   → returned to /services automatically.
       */
      const pendingRoute = sessionStorage.getItem("app3_post_login_target");

      if (pendingRoute && pendingRoute.startsWith("/")) {

        sessionStorage.removeItem("app3_post_login_target");

        if (router.currentRoute.value?.fullPath !== pendingRoute) {
          router.replace(pendingRoute).catch(() => {});
        }
      }

    } else {
      /**
       * No active oauth2 session.
       * The application will remain in anonymous mode.
       *
       * Login will only be triggered when accessing protected routes.
       */
      debugLog("No authenticated oauth2 session detected");
    }

  } catch (error) {

    /**
     * If authentication check fails,
     * the app still loads but user remains anonymous.
     */
    debugError("Auth initialization failed:", error);
  }


  /**
   * ---------------------------------------------------------
   * 4️⃣ Create and mount Vue app
   * ---------------------------------------------------------
   * The Vue application starts after authentication check
   * so the store already knows whether a user is logged in.
   */
  const app = createApp(App);

  app
    .use(store)
    .use(router)
    .use(VueToast)
    .use(VueSweetalert2)
    .use(alerts)
    .use(ApiService)
    .use(Loading)
    .use(HTTP)
    .use(Loader)
    .mount("#app");

  debugLog("Vue application mounted");
}

// Start application
initApp();

export default {};
